'use client'

import { ChoiceChip } from '@/components/molecules/choice-chip.client'
import { OrderField } from '@/components/sections/order/order-field.client'
import type { CreatorPick } from '@/lib/creator'

/**
 * "Te rolki mają powstać z tego filmu" (spec §8 · G8).
 *
 * The one control on this screen that is about ANOTHER line, which is why it
 * cannot live in the creator: `/pl/kreator` configures one offering at a time
 * and has nothing to point at. It appears here, on the order screen, and only
 * when there is a film in the basket to point AT.
 *
 * Two named buttons rather than a tick box, and deliberately so even when the
 * basket holds exactly one film. The alternative is not "nothing" — it is "own
 * footage", which costs a link and possibly a surcharge — and a checkbox that
 * silently swaps one obligation for another hides the trade. Spelling both
 * options out means the row below (the link, the minute count) appearing and
 * disappearing is something the person did rather than something that happened.
 *
 * It was a dropdown until the order screen was rebuilt around pressing rather
 * than picking from lists. Two options behind a click was the clearest case of
 * a menu standing in for a pair of buttons.
 */

/** Own footage. Not a real line id, so it can never collide with one. */
const OWN = 'wlasna'

export function FromFilmField({
  pick,
  films,
  onPatch,
}: {
  pick: CreatorPick
  /** The films in this basket, in basket order. Never includes this line. */
  films: { lineId: string; label: string }[]
  onPatch: (next: Partial<CreatorPick>) => void
}) {
  // A pointer at a film that has left the basket falls back to "own footage",
  // which is exactly what the server does with it — see `filmFor`. The screen
  // must not show a chosen value the order would not carry.
  const known = films.some((film) => film.lineId === pick.fromFilmLineId)
  const value = known && pick.fromFilmLineId ? pick.fromFilmLineId : OWN

  return (
    <OrderField
      wide
      label="Z czego montujemy?"
      hint={
        value === OWN
          ? 'Wklej link do surówki poniżej i podaj jej długość.'
          : 'Bez osobnej surówki i bez drugiej dopłaty za nią. Rolki wycinamy z zaakceptowanej wersji filmu, ten sam montażysta — więc ich termin rusza po jego ukończeniu.'
      }
    >
      <div className="flex flex-wrap gap-2">
        <ChoiceChip pressed={value === OWN} onClick={() => onPatch({ fromFilmLineId: null })}>
          Z własnej surówki
        </ChoiceChip>
        {films.map((film) => (
          <ChoiceChip
            key={film.lineId}
            pressed={value === film.lineId}
            onClick={() => onPatch({ fromFilmLineId: film.lineId })}
            title={film.label}
            className="max-w-full"
          >
            Z filmu: {film.label}
          </ChoiceChip>
        ))}
      </div>
    </OrderField>
  )
}
